import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import API from '../api/Api';
import i18n from '../i18n';

// ── Thunks ──────────────────────────────────────────────────────────────────

/** Fetch all modifier groups for the authed venue */
export const fetchModifierGroups = createAsyncThunk(
    'modifiers/fetchGroups',
    async (_, thunkAPI) => {
        try {
            const result = await API.getData('modifier/groups');
            const response = result.data;
            if (response.status) {
                return response.data;
            }
            return thunkAPI.rejectWithValue({ error: response.message });
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

/** Create a new modifier group */
export const createModifierGroup = createAsyncThunk(
    'modifiers/createGroup',
    async (formData, thunkAPI) => {
        try {
            const result = await API.postData('modifier/groups', formData);
            const response = result.data;
            if (response.status) {
                toast.success(response.message || i18n.t('modifiers.toast_saved'));
                return response.data;
            }
            return thunkAPI.rejectWithValue({ error: response.message });
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

/** Update an existing modifier group (replaces its options) */
export const updateModifierGroup = createAsyncThunk(
    'modifiers/updateGroup',
    async ({ id, ...formData }, thunkAPI) => {
        try {
            const result = await API.putData(`modifier/groups/${id}`, formData);
            const response = result.data;
            if (response.status) {
                toast.success(response.message || i18n.t('modifiers.toast_updated'));
                return response.data;
            }
            return thunkAPI.rejectWithValue({ error: response.message });
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

/** Soft-delete a modifier group */
export const deleteModifierGroup = createAsyncThunk(
    'modifiers/deleteGroup',
    async (id, thunkAPI) => {
        try {
            const result = await API.deleteData(`modifier/groups/${id}`);
            const response = result.data;
            if (response.status) {
                toast.success(response.message || i18n.t('modifiers.toast_deleted'));
                return id;
            }
            return thunkAPI.rejectWithValue({ error: response.message });
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

/** Fetch the modifier groups currently attached to a dish */
export const fetchDishGroups = createAsyncThunk(
    'modifiers/fetchDishGroups',
    async (subcategoryId, thunkAPI) => {
        try {
            const result = await API.getData(`modifier/for-dish/${subcategoryId}`);
            const response = result.data;
            if (response.status) {
                return response.data;
            }
            return thunkAPI.rejectWithValue({ error: response.message });
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

/** Attach (replace) the modifier groups for a dish */
export const attachGroupsToDish = createAsyncThunk(
    'modifiers/attachGroupsToDish',
    async ({ subcategory_id, group_ids }, thunkAPI) => {
        try {
            const result = await API.putData('modifier/attach', { subcategory_id, group_ids });
            const response = result.data;
            if (response.status) {
                return { subcategory_id, group_ids };
            }
            return thunkAPI.rejectWithValue({ error: response.message });
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

// ── Composed thunks ──────────────────────────────────────────────────────────

/** Save (create or update) then re-fetch the list and close the modal */
export const saveModifierGroup = (formData) => async (dispatch) => {
    let res;
    if (formData.id) {
        res = await dispatch(updateModifierGroup(formData));
    } else {
        res = await dispatch(createModifierGroup(formData));
    }
    if (res?.meta?.requestStatus === 'fulfilled') {
        dispatch(closeGroupModal());
    }
    return dispatch(fetchModifierGroups());
};

/** Delete then re-fetch the list */
export const deleteAndRefresh = (id) => async (dispatch) => {
    const res = await dispatch(deleteModifierGroup(id));
    if (res?.meta?.requestStatus === 'fulfilled') {
        return dispatch(fetchModifierGroups());
    }
};

// ── Slice ────────────────────────────────────────────────────────────────────

const modifierState = {
    isFetching: false,
    isError: false,
    errorMessage: '',
    groups: [],
    /** null = closed; {} = new; { ...group } = edit */
    editingGroup: null,
    /** id being confirmed for delete, or null */
    confirmDeleteId: null,
    /** IDs of groups currently attached to the dish being edited; null = not loaded */
    dishGroupIds: null,
    isAttaching: false,
};

const ModifierSlice = createSlice({
    name: 'modifiers',
    initialState: modifierState,
    reducers: {
        openNewGroup(state) {
            state.editingGroup = {};
        },
        openEditGroup(state, { payload }) {
            state.editingGroup = payload;
        },
        closeGroupModal(state) {
            state.editingGroup = null;
        },
        setConfirmDeleteId(state, { payload }) {
            state.confirmDeleteId = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchModifierGroups
            .addCase(fetchModifierGroups.pending, (state) => {
                state.isFetching = true;
                state.isError = false;
            })
            .addCase(fetchModifierGroups.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.groups = payload;
            })
            .addCase(fetchModifierGroups.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload?.error || 'Failed to load modifier groups';
                toast.error(state.errorMessage);
            })
            // createModifierGroup
            .addCase(createModifierGroup.pending, (state) => { state.isFetching = true; })
            .addCase(createModifierGroup.fulfilled, (state) => { state.isFetching = false; })
            .addCase(createModifierGroup.rejected, (state, { payload }) => {
                state.isFetching = false;
                toast.error(payload?.error || i18n.t('modifiers.toast_create_error'));
            })
            // updateModifierGroup
            .addCase(updateModifierGroup.pending, (state) => { state.isFetching = true; })
            .addCase(updateModifierGroup.fulfilled, (state) => { state.isFetching = false; })
            .addCase(updateModifierGroup.rejected, (state, { payload }) => {
                state.isFetching = false;
                toast.error(payload?.error || i18n.t('modifiers.toast_update_error'));
            })
            // deleteModifierGroup
            .addCase(deleteModifierGroup.pending, (state) => { state.isFetching = true; })
            .addCase(deleteModifierGroup.fulfilled, (state) => {
                state.isFetching = false;
                state.confirmDeleteId = null;
            })
            .addCase(deleteModifierGroup.rejected, (state, { payload }) => {
                state.isFetching = false;
                toast.error(payload?.error || i18n.t('modifiers.toast_delete_error'));
            })
            // fetchDishGroups
            .addCase(fetchDishGroups.pending, (state) => {
                state.dishGroupIds = null;
            })
            .addCase(fetchDishGroups.fulfilled, (state, { payload }) => {
                // for-dish returns [{ group: {id,...}, options:[...] }]
                state.dishGroupIds = (payload || []).map(g => g.group?.id ?? g.id);
            })
            .addCase(fetchDishGroups.rejected, (state) => {
                state.dishGroupIds = [];
            })
            // attachGroupsToDish
            .addCase(attachGroupsToDish.pending, (state) => { state.isAttaching = true; })
            .addCase(attachGroupsToDish.fulfilled, (state, { payload }) => {
                state.isAttaching = false;
                state.dishGroupIds = payload.group_ids;
            })
            .addCase(attachGroupsToDish.rejected, (state, { payload }) => {
                state.isAttaching = false;
                toast.error(payload?.error || i18n.t('modifiers.toast_attach_error'));
            });
    },
});

export const { openNewGroup, openEditGroup, closeGroupModal, setConfirmDeleteId } = ModifierSlice.actions;
export const selectModifiers = (state) => state.modifiers;
export default ModifierSlice.reducer;
